<script lang="ts">
	import { goto } from '$app/navigation'
	import { route } from '$lib/ROUTES'
	import FormContainer from '$lib/components/form/FormContainer.svelte'
	import FormHeader from '$lib/components/form/FormHeader.svelte'
	import { illustrationPosition } from '$lib/components/layouts/illustrationPosition'
	import ReCaptcha from '$lib/components/recaptcha/ReCaptcha.svelte'
	import CheckboxAcceptTerms from '$lib/components/signUp/CheckboxAcceptTerms.svelte'
	import SignUpAccountForm from '$lib/components/signUp/SignUpAccountForm.svelte'
	import SignUpAffiliatePublicId from '$lib/components/signUp/SignUpAffiliatePublicId.svelte'
	import type { RegistrationForm } from '$lib/domain/signUp/registrationForm'
	import { nodeApi } from '$lib/settings'
	import { login } from '@helpers/settings'
	import { _ } from '@repo/i18n'
	import { ToastType, handleApiErrors, toasts, user } from '@repo/ui'
	import type { ErrorsV1ErrorResponse } from '@sdk/Api'
	import { AffiliatesV1RegistrationErrorCode } from '@sdk/Api'
	import { registeredEmail } from '@store/registeredEmail'
	import { RegistrationStatus, registrationStatus } from '@store/registrationStatus'
	import { webName } from '@store/webName'
	import { onDestroy, onMount } from 'svelte'
	import { run } from 'svelte/legacy'

	interface Props {
		form: RegistrationForm
		loading?: boolean
		onContinue?: () => void
	}

	let { form = $bindable(), loading = $bindable(), onContinue = null }: Props = $props()

	let recaptcha: ReCaptcha = $state(null)

	function handleSignUpSubmit(event: CustomEvent) {
		if ($user) {
			onContinue()
		} else {
			loading = true

			nodeApi.usersV1UserRegistrationService
				.userRegistrationServiceRegisterUser({
					...form.user,
					recaptchaToken: event.detail,
				})
				.then(() => {
					registrationStatus.set(RegistrationStatus.InProgress)
					login(form.user.email, form.user.password).then((user) => {
						if (user) {
							onContinue()
						}
					})
				})
				.catch((response) => {
					if (response.status === 403) {
						toasts.add({
							type: ToastType.error,
							title: $_('error.title'),
							text: $_('error.bot'),
						})

						if (window?.['Intercom']) {
							window?.['Intercom']?.('showNewMessage')
						}
					} else {
						const error: ErrorsV1ErrorResponse = response.error
						error?.errors.forEach((error) => {
							if (
								error.code ===
								AffiliatesV1RegistrationErrorCode.REGISTRATION_ERROR_CODE_USER_ALREADY_REGISTERED
							) {
								$registeredEmail = form.user.email
								goto(route('/'))
							} else {
								handleApiErrors(response)
							}
						})
					}
				})
				.finally(() => {
					loading = false
				})
		}
	}

	export function handleSubmit() {
		recaptcha.handleVerify()
	}

	function handleCaptchaError(event: CustomEvent) {
		toasts.add({
			type: ToastType.error,
			title: $_('error.title'),
			text: event.detail,
		})
	}

	run(() => {
		if (form?.user) {
			form.affiliate.name = form.user.name
		}
	})

	onMount(() => {
		illustrationPosition.set('top')
	})

	onDestroy(() => {
		illustrationPosition.set('bottom')
	})
</script>

<FormHeader headline={$_('signUpAffiliate.welcome', { values: { webName: $webName } })} />

<FormContainer>
	<SignUpAccountForm bind:form={form.user} />

	<div class="mt-10"></div>

	<SignUpAffiliatePublicId name={form.user.name} bind:publicId={form.affiliate.publicId} />

	<div class="mt-10"></div>
</FormContainer>

<CheckboxAcceptTerms termsHref={route('/terms-conditions')} bind:checked={form.acceptedTerms} />

<ReCaptcha
	bind:this={recaptcha}
	bind:loading
	on:error={handleCaptchaError}
	on:success={handleSignUpSubmit}
/>

<div class="mt-10"></div>
